import type { DashboardObligation } from '../../lib/instance-dashboard-obligations';
import React, { useId, useMemo, useState } from 'react';
import { AlertCircle, AlertTriangle, CheckCircle2, ChevronDown, Info } from 'lucide-react';
import { cn } from '../SharedComponents';
import {
  formatHiddenInstanceAlertSummary,
  INSTANCE_EXECUTIVE_ALERT_SEVERITY_LABELS,
  limitInstanceExecutiveAlerts,
  type InstanceExecutiveAlert
} from '../../lib/operational-control/instance-executive-alerts';

const severityClass = (severity: InstanceExecutiveAlert['severity']): string =>
  severity === 'CRITICAL'
    ? 'border-red-500/30 bg-red-500/15 text-red-200'
    : severity === 'ATTENTION'
      ? 'border-amber-500/30 bg-amber-500/15 text-amber-200'
      : 'border-sky-500/30 bg-sky-500/15 text-sky-200';

const CompactAlert = ({ alert, compact }: { alert: InstanceExecutiveAlert; compact?: boolean }) => (
  <div
    data-alert-code={alert.code}
    data-alert-visibility="visible"
    title={`${alert.detail} ${alert.affectsScore ? 'Afecta la lectura operativa.' : 'No afecta el score.'}`}
    aria-label={`${alert.label}. ${alert.detail}`}
    className={compact ? 'flex min-w-0 items-center gap-2 rounded-xl border border-slate-800 bg-slate-900/60 px-2 py-1.5' : 'flex items-center gap-2 rounded-lg border border-slate-800/50 bg-slate-900/50 px-2.5 py-1.5'}
  >
    {compact && (alert.severity === 'CRITICAL' ? <AlertCircle size={16} className="shrink-0 text-red-300" aria-hidden="true" /> : alert.severity === 'ATTENTION' ? <AlertTriangle size={16} className="shrink-0 text-amber-300" aria-hidden="true" /> : <Info size={16} className="shrink-0 text-sky-300" aria-hidden="true" />)}
    <span className={cn(compact ? 'min-w-0 flex-1 truncate text-xs font-semibold text-white' : 'rounded border px-1.5 py-0.5 text-[8px] font-black uppercase tracking-wide', !compact && severityClass(alert.severity))}>{alert.label}</span>
    {!compact && <p className="min-w-0 truncate text-[9px] font-bold leading-tight text-white/60">{alert.detail}</p>}
    {compact && <span className={cn('shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold', severityClass(alert.severity))}>{INSTANCE_EXECUTIVE_ALERT_SEVERITY_LABELS[alert.severity]}</span>}
  </div>
);

const DetailedAlert = ({ alert, compact }: { alert: InstanceExecutiveAlert; compact?: boolean }) => (
  <article
    data-alert-code={alert.code}
    data-alert-visibility="hidden"
    className={cn('rounded-xl border border-slate-800 bg-slate-900/70 p-3', compact && 'p-2 [&_dl]:text-xs [&_strong]:text-xs [&_span]:text-xs [&_dt]:text-slate-400')}
  >
    <div className="flex flex-col items-start gap-1.5 sm:flex-row sm:items-center sm:justify-between">
      <strong className="text-[10px] font-black uppercase tracking-wide text-white">{alert.label}</strong>
      <span className={cn('rounded border px-2 py-0.5 text-[8px] font-black uppercase tracking-wide', severityClass(alert.severity))}>
        {INSTANCE_EXECUTIVE_ALERT_SEVERITY_LABELS[alert.severity]}
      </span>
    </div>
    <dl className={cn('mt-2 grid gap-1.5 text-[9px] leading-relaxed text-slate-300 sm:grid-cols-[92px_1fr]', compact && 'mt-1 gap-1')}>
      <dt className="font-black uppercase tracking-wide text-slate-500">Periodo</dt><dd>{alert.periodLabel}</dd>
      <dt className="font-black uppercase tracking-wide text-slate-500">Descripción</dt><dd>{alert.detail}</dd>
      <dt className="font-black uppercase tracking-wide text-slate-500">Dato activador</dt><dd>{alert.triggerData}</dd>
      <dt className="font-black uppercase tracking-wide text-slate-500">Efecto</dt><dd>{alert.affectsScore ? 'Afecta el score.' : 'No afecta el score.'}</dd>
    </dl>
  </article>
);

export const InstanceExecutiveAlertsPanel = ({ alerts, limit = 3, compact = false, operationalOnly = false, onOpenAlert }: { alerts: InstanceExecutiveAlert[]; limit?: number; compact?: boolean; operationalOnly?: boolean; onOpenAlert?: (alert: DashboardObligation) => void }) => {
  const [expanded, setExpanded] = useState(false);
  const generatedId = useId();
  const panelId = `instance-hidden-alerts-${generatedId.replace(/:/g, '')}`;
  const limited = useMemo(() => limitInstanceExecutiveAlerts(alerts, limit), [alerts, limit]);
  const hiddenSummary = useMemo(() => formatHiddenInstanceAlertSummary(limited.hidden), [limited.hidden]);
  const toggleExpanded = () => setExpanded((current) => !current);
  const handleToggleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      toggleExpanded();
    }
  };

  if (operationalOnly) return (
    <section data-instance-executive-alerts aria-label="Alertas operativas" className="rounded-2xl border border-slate-700 bg-slate-950 p-3">
      <h3 className="flex items-center gap-2 text-sm font-bold text-white"><AlertTriangle size={17} className="text-amber-300" />ALERTAS OPERATIVAS <span className="text-xs text-amber-300">VENCIDOS · {alerts.length}</span></h3>
      {alerts.length === 0 ? <p className="mt-2 flex items-center gap-2 text-sm text-emerald-300"><CheckCircle2 size={16} />Sin capturas vencidas. Todas las obligaciones exigibles están al día.</p> : <ul className="mt-2 space-y-2">
        {alerts.map(alert => <li key={alert.code} data-alert-code={alert.code} className="flex flex-col gap-2 rounded-xl border border-slate-800 bg-slate-900 p-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0"><strong className="break-words text-sm text-white">{alert.label} · {alert.periodLabel}</strong><p className="mt-1 break-words text-sm text-amber-200">{alert.detail}</p><p className="mt-1 text-xs text-slate-300">{alert.triggerData}</p></div>
          {onOpenAlert && <button type="button" className="shrink-0 rounded-lg border border-sky-700 bg-sky-950 px-3 py-2 text-xs font-bold text-sky-100" onClick={() => onOpenAlert(alert as DashboardObligation)}>Ver indicador y acciones</button>}
        </li>)}
      </ul>}
    </section>
  );
  if (compact && alerts.length === 0) return null;

  return (
    <section data-instance-executive-alerts aria-label="Alertas operativas" className={compact ? 'min-w-0' : 'flex h-full flex-col rounded-2xl border border-slate-800 bg-slate-950 p-4 shadow-xl'}>
      <p className={compact ? 'mb-1 flex items-center gap-2 text-xs font-semibold text-amber-300' : 'mb-2 flex items-center gap-1.5 text-[9px] font-black uppercase tracking-[0.2em] text-amber-400'}><AlertTriangle size={15} /> Alertas <span className="rounded-full bg-amber-400/10 px-2 py-0.5 tabular-nums">{alerts.length}</span></p>
      <div className={cn('flex-1 space-y-1.5', compact && 'space-y-1')}>
        {limited.visible.length > 0 ? limited.visible.map((alert) => <CompactAlert key={alert.code} alert={alert} compact={compact} />) : (
          <div className="flex h-full items-center justify-center gap-2 opacity-50">
            <CheckCircle2 size={16} className="text-emerald-400" />
            <span className="text-[9px] font-bold uppercase tracking-widest text-emerald-400">Sin alertas</span>
          </div>
        )}
        {limited.hidden.length > 0 && (
          <>
            <button
              data-hidden-alerts-toggle
              type="button"
              aria-expanded={expanded}
              aria-controls={panelId}
              onClick={toggleExpanded}
              onKeyDown={handleToggleKeyDown}
              className="flex w-full items-center justify-between gap-2 rounded-lg border border-slate-700 bg-slate-900 px-2.5 py-1.5 min-h-11 text-left text-xs font-black uppercase tracking-wider text-slate-300 transition hover:border-sky-500/50 hover:text-sky-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 focus-visible:ring-offset-2 focus-visible:ring-offset-slate-950"
            >
              <span>{hiddenSummary}</span>
              <ChevronDown aria-hidden="true" size={13} className={cn('shrink-0 transition-transform', expanded && 'rotate-180')} />
            </button>
            {expanded && (
              <div id={panelId} data-hidden-alerts-panel className={cn('space-y-2 pt-1', compact && 'space-y-1.5')} aria-label="Alertas adicionales">
                {limited.hidden.map((alert) => <DetailedAlert key={alert.code} alert={alert} compact={compact} />)}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
};

export default InstanceExecutiveAlertsPanel;
